import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";

import {
  AppAccess,
  Dialog,
  Disclose,
  EmptyState,
  Headline,
  Loading,
  Refusal,
  Tooltip,
} from "@lopie/kernel-ui";

import AppDrawer from "../components/AppDrawer.jsx";
import QaPanel from "./qa.jsx";
import { ACCESS_LINKS } from "./accessQueue.js";
import { EGRESS_DOES_NOT_PROVE, NO_EGRESS, approvedEgress } from "./sandbox.js";
import { AdoptDialog, RevertDialog } from "./adopt.jsx";
import {
  InstallDialog,
  InstanceRow,
  RemoveDialog,
  useDeployments,
} from "./deploy.jsx";

/**
 * Every app: what it may touch, what is deployed, and how to add or remove one.
 *
 * This was two screens. "Library" answered what can I install and what is
 * installed; "Apps" answered what may each app touch. An app you had
 * installed was therefore a different kind of thing on each — a deployment on
 * one, a set of permissions on the other, and a sign-in on the Portal — and
 * the two screens showed the same eleven apps in two orders under two names.
 * The Library had a Remove button; Apps, which sounds like where your apps
 * are, had no idea a deployment existed.
 *
 * So there is one card per app and it carries all of it. An app with nothing
 * deployed still shows its ceiling; an app that cannot be installed simply
 * has no Install button; an app that is only a deployment target has no
 * agents to list. None of those is an error and none of them is hidden.
 *
 * The two halves still come from two different APIs and always will — the
 * kernel says what an app may touch, the control plane says what is running
 * and is the only thing that can change it — and that is a fact about the
 * system rather than about how somebody looks for their apps.
 *
 * The manifest is the document the whole authorization model rests on, and
 * until this screen nothing rendered it — `get_manifest(app)` takes an id you
 * already have, which is fine for enforcement and useless for "what apps
 * exist". So the people governed by these ceilings could not read them.
 *
 * ## The card is a summary and the drawer is the ceiling (LO-111)
 *
 * All of the above was on the card, and the result was a page **6,617 pixels
 * tall** for fourteen apps: eight labelled blocks each, most of them a
 * sentence explaining an empty list. Every sentence true, and the four apps
 * somebody actually operates indistinguishable from the ten that came built
 * in.
 *
 * So the card answers the four things a person asks in order — *what is this,
 * how much may it do, is any of it running, does it reach further with nobody
 * there* — and `AppDrawer` answers everything else, in full. The register is
 * a grid you can scan; the ceiling is one click in.
 *
 * Two things stay on the face of the card, because collapsing them would be
 * collapsing the point:
 *
 * **A manifest that does not parse.** It is enforcing nothing, and this is
 * the one screen where you would never find that out if it were quietly
 * dropped. It renders as a card-sized error, not a chip.
 *
 * **Reaching further with nobody there.** The kernel computes it and until
 * this screen nothing rendered it at all; a warning that needs a click to
 * find is a warning nobody finds. The chip is on the card and the sentence is
 * in the drawer.
 *
 * **The console appears in its own list.** An operator console absent from
 * the register it displays would be a back door with no entry.
 */

/**
 * Confirm archiving a custom app — reversible, so no type-to-confirm.
 *
 * `RemoveDialog` makes somebody type the name because removing an instance
 * takes real infrastructure down. Archiving takes nothing down: the ceiling,
 * the client, anything provisioned are all unmoved, and the same call with
 * `archived: false` undoes it. A confirmation this light for a decision this
 * cheap is the honest match, not a formality skipped.
 */
function ArchiveDialog({ open, app, busy, failure, onClose, onArchive }) {
  const [reason, setReason] = useState("");
  const name = app?.label || app?.app_type || app?.app || "";

  const submit = () => onArchive(reason.trim());

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Archive {name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Off the Custom apps list and onto Archived. Nothing about what
            this app may touch changes — its ceiling, its client and its
            history all stay exactly as they are — and unarchiving is one
            click from the Archived tab.
          </Typography>
          <TextField
            label="Why (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            disabled={busy}
            autoFocus
          />
          {failure ? (
            <Alert severity="error" variant="outlined">
              {failure.body?.detail || failure.message}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          Archive
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * What is deployed of one app, the button that removes one, and the history.
 *
 * The rows never go away — a removal is recorded, not forgotten, because "we
 * never installed that" and "we installed it and took it down" are different
 * answers and only one of them is in the row. But four removed vaults above
 * the one that is running buries the thing somebody came to look at, so the
 * history folds away and says how much of it there is.
 *
 * Shut by default and shut again on every load. An expanded history is a
 * thing you asked for about one app, not a preference about this screen.
 */
function Deployments({ entry, origin, deployments, nameOf, canRemove, onRemove,
                      onInstall, onUpdate, onAutoUpdate, copies, onReplace, onRevert }) {
  const [showGone, setShowGone] = useState(false);

  const live = deployments.filter((d) => d.desiredState !== "absent");
  const gone = deployments.filter((d) => d.desiredState === "absent");

  return (
    <>
      {live.length ? (
        <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mb: 1.5 }}>
          {live.map((d, n) => (
            <InstanceRow
              key={d.name}
              instance={d}
              appName={nameOf(d.app)}
              first={n === 0}
              onRemove={canRemove ? onRemove : null}
              // The catalog's default, which is what a fresh install would
              // get. Offering every published version here would be a second
              // install form on a screen that is a status list.
              latest={entry?.defaultVersion}
              onUpdate={canRemove ? onUpdate : null}
              onAutoUpdate={canRemove ? onAutoUpdate : null}
              copies={copies}
              onReplace={canRemove ? onReplace : null}
              onRevert={canRemove ? onRevert : null}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {entry
            ? "Nothing deployed. Installing one builds it in your organization's own AWS account."
            : origin === "request"
              // A ceiling somebody approved, with no catalog row yet — not
              // the same fact as a platform app that was never installable.
              // The publish pipeline (LO-127-ish) fills this in further once
              // it can say "building" or "failed" rather than only "not yet."
              ? "Approved, and not published yet. Nothing to install until a release exists."
              : "Nothing deployed, and this app is not one you install — it came built in."}
        </Typography>
      )}

      {onInstall ? (
        <Button size="small" variant="contained" onClick={onInstall}>
          Install
        </Button>
      ) : null}

      {gone.length ? (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setShowGone(!showGone)}
            sx={{ px: 0, minWidth: 0 }}
          >
            {showGone
              ? "Hide what was removed"
              : `${gone.length} removed before — show`}
          </Button>
          {showGone ? (
            <Stack
              divider={<Divider flexItem />}
              spacing={1.5}
              sx={{ mt: 1 }}
            >
              {gone.map((d, n) => (
                <InstanceRow
                  key={d.name}
                  instance={d}
                  appName={nameOf(d.app)}
                  first={n === 0}
                  onRemove={null}
                />
              ))}
            </Stack>
          ) : null}
        </Box>
      ) : null}
    </>
  );
}

/**
 * Words people already know, for things only this platform calls that.
 *
 * ## The card was written in the platform's dictionary
 *
 * It said `4 VERBS · MAY WRITE`, `1 TYPES · FOR A PERSON`, `0 TYPES ·
 * UNATTENDED`. Every one of those is exact, and none of them is a sentence
 * anybody outside this repo would say. A person looking at the Apps screen is
 * asking *what can this thing do to my data* — and three counts of two nouns
 * they have never met is not an answer, it is a quiz.
 *
 * So the card says it in English. `oncall` **can change messages, calls and
 * people**; `ledger-review` **can only look**. The exact verbs and the exact
 * grants are in the drawer, unchanged, because that is where somebody who
 * needs `person.hold` goes to find it.
 *
 * ## The plurals come from the kernel
 *
 * `GET /schema` carries `collection` per type — `Person` → `people`,
 * `Invoice` → `invoices` — which is the plural, already written down, in the
 * one place that cannot drift from what the types actually are. A map in this
 * file would be a second copy of the schema and wrong the first time somebody
 * adds a type.
 *
 * A verb whose noun is not a record type is a platform capability —
 * `audit.read`, `install.configure` — and the schema has no plural for it.
 * Those get a naive `+ s`, skipped when the word already ends in one, which
 * is enough English for `connector`, `install`, `manifest`, `agent` and
 * `call` and leaves `access` alone. It is a rendering rule, not a governance
 * document: nothing here decides anything, it only names it.
 */

/** `Person` → `people`, from `/schema`, else a careful `+ s`. */
export function plural(noun, collections) {
  const known = collections[String(noun).toLowerCase()];
  if (known) return known;
  const word = String(noun);
  // `access` already ends in one, and `accesss` is how a screen stops being
  // believed. The rest of the platform nouns take a plain `s`.
  return /(?:s|x|z|ch|sh)$/i.test(word) ? word : `${word}s`;
}

/** "messages, calls and people" — a list a person reads rather than parses. */
export function english(words) {
  const list = [...new Set(words)];
  if (!list.length) return "";
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

/** The things a set of verbs is about, named the way a person would name them. */
export function nounsOf(verbs, collections) {
  return english(
    (verbs || []).map((verb) => plural(String(verb).split(".")[0], collections)),
  );
}

/** The things a set of grants is about. */
export function typesOf(grants, collections) {
  return english(
    (grants || []).map((grant) => plural(grant.type, collections)),
  );
}

/**
 * One app, at a glance.
 *
 * What it is, what it can change, what it can see, what it does with nobody
 * watching, and what you have running of it. The whole card is the control
 * that opens the rest.
 */
function AppCard({
  app,
  entry,
  deployments,
  collections,
  canInstall,
  canRemove,
  onInstall,
  onRemove,
  onOpen,
  onArchive,
  onUnarchive,
  unarchiveBusy,
}) {
  if (app.unparseable) {
    return (
      <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
        {/* Full size, and not a chip. A manifest that does not parse is
            enforcing nothing, and this is the one screen where you would
            never find that out if it were quietly dropped. */}
        <Typography variant="h2" sx={{ fontFamily: "monospace", mb: 1 }}>
          {app.app}
        </Typography>
        <Alert severity="error" variant="outlined">
          <AlertTitle>This manifest does not parse</AlertTitle>
          {app.detail}
        </Alert>
      </Paper>
    );
  }

  const running = deployments.filter((d) => d.desiredState !== "absent");
  const live = running.length;
  // Removed, not forgotten. "Never installed" and "installed and taken down"
  // are different answers and the card said the same thing for both.
  const gone = deployments.filter((d) => d.desiredState === "absent").length;
  const wider = (app.service_wider_than_user || []).length;
  const widerWrites = (app.service_actions || []).filter(
    (a) => !(app.actions || []).includes(a),
  ).length;
  const reaches = wider + widerWrites;
  const egress = approvedEgress(app);

  return (
    // Not a <button>. It was one, and it contained the Install and Remove
    // buttons — a button inside a button, which is invalid HTML and which
    // React refuses in the dev console.
    //
    // It matters more here than on a card that merely opens a page. This
    // screen's own gate text says installing "builds real infrastructure in
    // your organization's own AWS account and costs money to run", and the
    // outer button's accessible name was the card's entire text content, so
    // a screen reader announced one control whose name contained the words
    // Install and Remove and whose activation did neither.
    //
    // Same fix as the Portal product cards: the *title* is the button and
    // stretches over the card with `::after`, so the hit area is the whole
    // card while the accessible name stays the app's name. Install and
    // Remove are ordinary siblings above that overlay.
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        // The anchor for the title's stretched hit area.
        position: "relative",
        backgroundColor: "background.paper",
        "&:hover": { borderColor: "primary.main" },
        // The card shows the focus, because the thing being focused is the
        // title and its own ring would circle two words in the corner of a
        // box the whole of which is clickable.
        "&:focus-within": {
          borderColor: "primary.main",
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", minWidth: 0 }}
      >
        {/* The app type, which is the name the Portal uses for the same app.
            This screen used to head the card with `app.app` — the key the
            manifest is stored under, which for every app but one is a Cognito
            client id. The two screens list the same apps and looked like
            different registers of different things.

            `app_type` is always populated: a manifest written before it
            existed falls back to its own key, which is what those manifests
            meant. So this never renders blank. */}
        {/* The name a person uses, and under it the id the kernel enforces
            against. Never one instead of the other: `app_type` is what a
            manifest is keyed by, what a provenance envelope carries and what
            decides every permission, and a console that showed only
            `On-Call Pager` would be being polite about the thing that
            actually governs.

            An app with no declared label shows its id as the heading, which
            is what every manifest meant before there was a field to say
            otherwise. */}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h2" sx={{ overflowWrap: "anywhere" }}>
            <Box
              component="button"
              type="button"
              onClick={() => onOpen(app)}
              sx={{
                font: "inherit",
                color: "inherit",
                textAlign: "left",
                background: "none",
                border: 0,
                p: 0,
                cursor: "pointer",
                // The card is the hit area; the title is the name.
                "&::after": { content: '""', position: "absolute", inset: 0 },
                // Handled by the card's `:focus-within` above.
                "&:focus-visible": { outline: "none" },
              }}
            >
              {app.label || app.app_type || app.app}
            </Box>
          </Typography>
          {app.label ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "ui-monospace, monospace" }}
            >
              {app.app_type || app.app}
            </Typography>
          ) : null}
        </Box>
        <Chip size="small" variant="outlined" label={`v${app.version}`} />
        {app.is_self ? (
          <Chip
            size="small"
            label="this console"
            variant="outlined"
            sx={{ color: "kernel.permitted", borderColor: "kernel.permitted" }}
          />
        ) : null}
      </Stack>

      {entry?.description ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {entry.description}
        </Typography>
      ) : null}

      <Stack spacing={0.75} sx={{ mt: 0.5, minWidth: 0 }}>
        <Plain
          label="Can change"
          title={
            app.actions?.length
              ? `In full: ${app.actions.join(", ")}. An app that declares none cannot write at all — the kernel refuses the verb, not the record.`
              : "This app declares no action at all, so there is no write the kernel would accept from it. Not a setting and not a habit."
          }
        >
          {app.actions?.length
            ? nounsOf(app.actions, collections)
            : "nothing — it can only look"}
        </Plain>

        <Plain
          label="Can see"
          title={
            app.reads?.length
              ? "What a signed-in person reaches through this app. Their own groups narrow it further on every request, so this is the most it could ever show them."
              : "No business record of any type, for anybody signed in. An app can still be useful — this one reaches the platform rather than the graph."
          }
        >
          {app.reads?.length
            ? typesOf(app.reads, collections)
            : "no records"}
        </Plain>

        {/* The hosts the app's server function was approved to call (LO-175),
            beside what it may read and change because egress is now part of
            what was granted. Always shown, and "none" is said rather than
            left blank: an approval that names no host is a fact about the
            approval, where a missing row would read as "not looked at". The
            tooltip carries what the line cannot show. */}
        <Plain label="Can call out to" title={EGRESS_DOES_NOT_PROVE} hoverable>
          {egress.none ? (
            NO_EGRESS
          ) : (
            <Box
              component="span"
              sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
            >
              {egress.hosts.slice(0, 3).join(", ")}
              {egress.hosts.length > 3
                ? ` and ${egress.hosts.length - 3} more`
                : ""}
            </Box>
          )}
        </Plain>

        {/* Only when there is something to say. Most apps never run with
            nobody there, and a row reading "nothing" on eight cards out of
            eleven is a row that teaches people to skip the section. */}
        {app.service_reads?.length || app.service_actions?.length ? (
          <Plain
            label="On its own"
            title="With nobody signed in behind a request, there is no second limit — only what the app asked for. This is what it reaches then, and it is declared separately and reviewed separately for that reason."
          >
            {[
              app.service_reads?.length
                ? `sees ${typesOf(app.service_reads, collections)}`
                : null,
              app.service_actions?.length
                ? `changes ${nounsOf(app.service_actions, collections)}`
                : null,
            ]
              .filter(Boolean)
              .join(", ")}
          </Plain>
        ) : null}
      </Stack>

      {/* `mt: auto` so the footer sits on the bottom edge of every card in
          the row regardless of how long the description ran. A row of cards
          whose last lines do not agree reads as a row of accidents. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mt: "auto",
          pt: 1.25,
          borderTop: 1,
          borderColor: "divider",
          flexWrap: "wrap",
          alignItems: "center",
          minWidth: 0,
        }}
      >
        {/* "came built in" was wrong and it was mine. It read as *this is
            part of the platform*, and it is true of `console`, `kernel`,
            `mcp` and `agent-os` and of nothing else — the same words sat
            under `example-remote`, which is an example, and under
            `crowcase`, which nobody has decided about. A label that flatters
            a leftover into a built-in is how a leftover survives a cleanup.

            What is actually true of all of them is narrower and duller: the
            library publishes no package for this app, so there is nothing to
            install. Whether it *should* exist is a decision, and this screen
            is not allowed to imply it has already been made. */}
        {/* Three kinds of app on this screen, and the footer used to tell
            two of them apart.

            An app the library publishes is installed and removed from here.
            An app that exists because `manifests/<name>.json` is committed is
            neither — nothing provisioned it, so there is nothing for Remove
            to take down, and **deleting its row from this console would be
            undone by the next `manifests/apply.py`**, which globs that
            directory and writes every file in it.

            That is the real reason there is no button, and it is a better one
            than "it is dangerous": the committed file is the source of truth,
            so retiring one of these is a change to the repo. `no package to
            install` was true and said none of that — it described what the
            app lacks instead of what it is. */}
        <Tooltip
          title={
            live
              ? "Deployments the control plane reports as present."
              : entry
                ? "The library publishes a package for this app and none is deployed."
                : "This app exists because a manifest for it is committed in the repo. Nothing provisioned it, so there is nothing here to remove — and a row deleted from this console comes back on the next `manifests/apply.py`. Retiring one is `kernel/scripts/retire_app.py`, beside deleting the file."
          }
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ borderBottom: "1px dotted", borderColor: "divider", cursor: "help" }}
          >
            {live
              ? `${live} running`
              : entry
                ? "nothing deployed"
                : "declared in the repo"}
            {gone
              ? ` · ${gone} removed`
              : null}
            {app.agents?.length
              ? ` · ${app.agents.length} agent${app.agents.length === 1 ? "" : "s"}`
              : null}
          </Typography>
        </Tooltip>
        {app.archived ? (
          <Tooltip
            title={
              `Archived ${new Date(app.archived.at).toLocaleDateString()}`
              + (app.archived.by ? ` by ${app.archived.by}` : "")
              + (app.archived.reason ? `: ${app.archived.reason}` : ".")
            }
          >
            <Chip size="small" variant="outlined" label="archived" />
          </Tooltip>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {/* Back on the card after a version without it. Installing is the
            verb this screen exists for — merging the Library into it was
            about making that discoverable — and a primary action you reach by
            first opening something else is one step worse than the two
            screens this replaced.

            `stopPropagation`, because the card around it is also a button and
            a click that both installs an app and opens a drawer is a click
            that did two things you asked for once. */}
        {/* Remove belongs beside Install and was only in the drawer.
            Everything Crossplane provisioned is something somebody can take
            down, and a card that offers to install one and not to remove it
            is a card that only helps in the direction that costs money.

            One live instance is removed from here. More than one and the
            card cannot say *which*, so it sends you to the drawer, where
            each row has its own button and its own name beside it. */}
        {canRemove && live === 1 ? (
          <Button
            size="small"
            color="inherit"
            onClick={() => onRemove(running[0])}
            // `position: relative` puts it above the title's stretched
            // `::after`. No `stopPropagation` any more: the card is not a
            // button, so there is no parent handler to defend against.
            sx={{ color: "kernel.refused", position: "relative" }}
          >
            Remove
          </Button>
        ) : null}
        {canRemove && live > 1 ? (
          <Typography variant="body2" sx={{ color: "kernel.refused" }}>
            open to remove one
          </Typography>
        ) : null}
        {/* Custom apps only, and only with nothing running — an app somebody
            approved and finished with, not one still doing something. It
            changes no permission; it moves this card off the list a person
            scans by default and onto Archived, and the same call undoes it. */}
        {onArchive ? (
          <Button
            size="small"
            color="inherit"
            onClick={onArchive}
            sx={{ position: "relative" }}
          >
            Archive
          </Button>
        ) : null}
        {onUnarchive ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onUnarchive}
            disabled={unarchiveBusy}
            startIcon={unarchiveBusy ? <CircularProgress size={14} /> : null}
            sx={{ position: "relative" }}
          >
            Unarchive
          </Button>
        ) : null}
        {canInstall ? (
          <Button
            size="small"
            variant="contained"
            onClick={onInstall}
            sx={{ position: "relative" }}
          >
            Install
          </Button>
        ) : null}
        {reaches ? (
          // On the card rather than in the drawer, because a warning that
          // needs a click to find is a warning nobody finds. The kernel
          // computes it and until this screen nothing rendered it at all.
          <Tooltip title="With no user behind a request there is no second limit — only what the app asked for. This app reaches further, or does more, unattended than any signed-in person gets through it. Open it for which types and which verbs.">
            <Chip
              size="small"
              variant="outlined"
              label="reaches further unattended"
              sx={{
                color: "kernel.attention",
                borderColor: "kernel.attention",
              }}
            />
          </Tooltip>
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * A plain label and a plain answer, on one line where there is room.
 *
 * The label is English and the value is English. `Stat` was the wrong shape
 * here — it is built for a number whose label has to carry its basis, and
 * none of these are numbers.
 */
function Plain({ label, title, children, hoverable = false }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "baseline", flexWrap: "wrap", minWidth: 0 }}
    >
      <Tooltip title={title}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            minWidth: 78,
            borderBottom: "1px dotted",
            borderColor: "divider",
            cursor: "help",
            // Above the card's stretched hit area, so a pointer reaches the
            // tooltip as well as the keyboard does. Off by default: the other
            // labels have always sat under it, and a click on them opens the
            // app. Turned on where the tooltip carries what a line cannot show.
            ...(hoverable ? { position: "relative" } : {}),
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Typography>
    </Stack>
  );
}

export default function Apps({ session, get, post, onEffective }) {
  const [apps, setApps] = useState(null);
  const [collections, setCollections] = useState({});
  const [failure, setFailure] = useState(null);
  const [open, setOpen] = useState(null);

  // Custom apps, kernel library, archived — three different questions about
  // the same register, and one page kept trying to answer all three at once.
  // `tab` is local and unsaved on purpose: which one you were looking at is
  // not a fact worth remembering across a reload, the same way the Portal's
  // own Apps/Services split works.
  const [tab, setTab] = useState(0);

  // Filters the tab you are on rather than the counts on the tabs themselves
  // — the badge is the size of the register, the grid is what matches. Kept
  // across a tab switch on purpose: narrowing to one name and then checking
  // whether it turns up in Kernel library too is a reasonable thing to do
  // and losing the search on every click would make it that.
  const [query, setQuery] = useState("");

  // Archiving a custom app, and unarchiving one. Both are `app.archive` on
  // the kernel — a status, not a permission change — so neither routes
  // through `useDeployments`, which is the control plane's half of this
  // screen and knows nothing about a manifest's own fields.
  const [archiving, setArchiving] = useState(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveFailure, setArchiveFailure] = useState(null);
  const [unarchivingType, setUnarchivingType] = useState(null);
  const [actionFailure, setActionFailure] = useState(null);

  const deploy = useDeployments({ session, get, post, onEffective });

  const load = useCallback(async () => {
    try {
      // Two reads, and only one of them may fail the screen. `/apps` is what
      // this page is; `/schema` is how it words the answer, and a kernel that
      // did not hand over its type list should cost a screen its plurals
      // rather than its content — `plural` falls back on its own.
      const body = await get("/apps");
      setApps(body.apps || []);
      setFailure(null);
      try {
        const schema = await get("/schema");
        const types = schema.types || schema;
        const list = Array.isArray(types)
          ? types
          : Object.entries(types).map(([type, t]) => ({ type, ...t }));
        setCollections(
          Object.fromEntries(
            list
              .filter((t) => t.type && t.collection)
              .map((t) => [String(t.type).toLowerCase(), t.collection]),
          ),
        );
      } catch {
        // Deliberately swallowed, and the only swallow on this screen. The
        // cost is `person` where `people` would have read better, which is
        // worse writing and not a wrong answer.
        setCollections({});
      }
    } catch (e) {
      setFailure(e);
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const archiveApp = async (reason) => {
    const type = archiving.app_type || archiving.app;
    setArchiveBusy(true);
    setArchiveFailure(null);
    try {
      await post(`/apps/${type}/archive`, { archived: true, reason });
      setArchiving(null);
      await load();
    } catch (e) {
      setArchiveFailure(e);
    } finally {
      setArchiveBusy(false);
    }
  };

  const unarchiveApp = async (type) => {
    setUnarchivingType(type);
    setActionFailure(null);
    try {
      await post(`/apps/${type}/archive`, { archived: false });
      await load();
    } catch (e) {
      setActionFailure(e);
    } finally {
      setUnarchivingType(null);
    }
  };

  // The kernel's answer is the one that must arrive. The control plane's can
  // fail on its own — it is a second API, reachable separately — and when it
  // does, this screen still says what every app may touch and says it cannot
  // reach the rest. A screen that refused to render either half because one
  // API was down would be two outages for the price of one.
  if (failure) {
    return <Refusal failure={failure} actor="This console" onRetry={load} />;
  }
  if (!apps) return <Loading label="Reading every app" />;

  const catalogFor = (appType) =>
    (deploy.catalog || []).find((e) => e.app === appType);

  // Apps you can do something with first: installable, or with something
  // deployed. Then the rest, which are the ones that came built in — real,
  // listed, and not what anybody opened this screen for.
  const order = (list) => [...list].sort((a, b) => {
    const key = (x) => {
      const type = x.app_type || x.app;
      const interesting = catalogFor(type)
        || deploy.deploymentsOf(type).length ? 0 : 1;
      return [interesting, type];
    };
    const [ai, an] = key(a);
    const [bi, bn] = key(b);
    return ai - bi || an.localeCompare(bn);
  });

  // A manifest that does not parse is enforcing nothing, and this is the one
  // screen where you would never find that out if it were quietly dropped —
  // so it is shown regardless of which tab is open, not sorted into one of
  // three lists that all assume `origin` parsed.
  const unparseable = apps.filter((a) => a.unparseable);
  const parseable = apps.filter((a) => !a.unparseable);

  // "Custom apps" and "kernel library" (LO-128-ish): the two documents an
  // app's ceiling can come from — `origin_of` in the kernel — read as the
  // two registers a person actually means by those words. A third, Archived,
  // for a custom app somebody finished with: off the list scanned by
  // default, kept rather than deleted, one click from coming back.
  const customApps = parseable.filter((a) => a.origin === "request" && !a.archived);
  const libraryApps = parseable.filter((a) => a.origin !== "request" && !a.archived);
  const archivedApps = parseable.filter((a) => a.archived);
  // An archived app is one somebody took off the register, and offering it as
  // what to run an installed app on would be offering what they retired.
  const retired = new Set(archivedApps.map((a) => a.app_type || a.app));
  const copiesFor = (app) => deploy.copiesFor(app).filter((c) => !retired.has(c.app));

  // The label if there is one, the id either way, and the sentence under the
  // title — the same three fields the card itself puts in front of you, so a
  // match here is never a surprise once you find the card it came from.
  const matchesQuery = (a) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [a.label, a.app_type, a.app, a.description]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(q));
  };

  const shown = order(
    (tab === 0 ? customApps : tab === 1 ? libraryApps : archivedApps)
      .filter(matchesQuery),
  );

  // Across every app, not per card: the one number this screen leads with.
  const running = apps.reduce(
    (n, a) =>
      n
      + deploy
        .deploymentsOf(a.app_type || a.app)
        .filter((d) => d.desiredState !== "absent").length,
    0,
  );

  const openType = open ? open.app_type || open.app : null;
  const missing = (deploy.catalog || []).filter(
    (e) => !apps.some((a) => (a.app_type || a.app) === e.app),
  );

  return (
    <Stack spacing={2}>
      {deploy.note ? (
        <Alert
          severity="info"
          variant="outlined"
          onClose={() => deploy.setNote(null)}
        >
          {deploy.note}
        </Alert>
      ) : null}

      {deploy.failure ? (
        // Named rather than swallowed. Without the control plane this screen
        // is still correct about permissions and simply blind to what is
        // running, which is a different thing from an app having nothing
        // deployed — and the two must not render the same.
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Cannot reach what is deployed</AlertTitle>
          Everything below is what each app is allowed to touch, which is
          right. What is running under each one is unknown for now, and
          Install and Remove are unavailable until it answers.
        </Alert>
      ) : null}

      {actionFailure ? (
        <Alert
          severity="error"
          variant="outlined"
          onClose={() => setActionFailure(null)}
        >
          {actionFailure.body?.detail || actionFailure.message}
        </Alert>
      ) : null}

      {/* Running is the figure, not declared. A manifest costs nothing and
          eleven of them is a fact about the repository; one app actually
          standing up in a customer's AWS account is a fact about the
          product. */}
      <Headline
        eyebrow="Standing up right now"
        figure={running.toLocaleString()}
        caption={
          running === 1
            ? `deployment, of ${apps.length} apps this kernel governs`
            : `deployments, of ${apps.length} apps this kernel governs`
        }
        basis="What the control plane reports as present. An app with a manifest and nothing deployed is a ceiling nobody is standing under — real, reviewed, and not running. The two are counted separately here because they are separate facts."
      />

      {/* The sentence that has to survive being the only one read is the
          second one: a ceiling is not a promise that any data exists. */}
      <Disclose
        summary="What an app is allowed to touch is the most it could ever reach, not what it holds."
        label="What this register is"
      >
        <Typography variant="body2" color="text.secondary">
          Every app this Kernel knows, with what it is allowed to touch and
          what you have running of it. The ceiling is one decision for every
          person who signs into that app, so your own groups narrow it further
          on every request — a declaration here can still return an empty page,
          and that would be about your groups rather than about the app.
        </Typography>
      </Disclose>

      {/* A manifest that does not parse first, regardless of which tab is
          open — see `unparseable`, above. */}
      {unparseable.length ? (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(auto-fit, minmax(300px, 1fr))",
            },
          }}
        >
          {unparseable.map((a) => (
            <AppCard key={a.app} app={a} deployments={[]} collections={{}} />
          ))}
        </Box>
      ) : null}

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={`Custom apps (${customApps.length})`} />
        <Tab label={`Kernel library (${libraryApps.length})`} />
        <Tab label={`Archived (${archivedApps.length})`} />
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
        {tab === 0
          ? "Built by an assistant, or approved from a request. Each one's ceiling was reviewed once, the same way as any other app in this kernel."
          : tab === 1
            ? "Committed in this repo — the console, the kernel's own apps, and every template the Library publishes. Retiring one is a change to manifests/, not a click here."
            : "Custom apps somebody archived: off the list above and kept rather than deleted. Nothing about the ceiling changed, and restoring one is a click."}
      </Typography>

      <TextField
        size="small"
        placeholder="Search this tab by name or id"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ maxWidth: 360 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      {tab === 2 && !archivedApps.length ? (
        <EmptyState title="Nothing archived">
          <Typography variant="body2">
            A custom app with nothing running gets an Archive button on its
            card. It moves here, keeps its ceiling exactly as approved, and
            comes back with one click.
          </Typography>
        </EmptyState>
      ) : shown.length === 0 ? (
        // The tab has apps; the search just found none of them here. A
        // different sentence from "nothing archived" on purpose — one is
        // about this register and the other is about what you typed.
        <EmptyState title={`No apps match "${query.trim()}" here`}>
          <Typography variant="body2">
            Nothing on this tab's name or id contains that. It may be on
            another tab —{" "}
            {[
              tab !== 0 ? "Custom apps" : null,
              tab !== 1 ? "Kernel library" : null,
              tab !== 2 ? "Archived" : null,
            ]
              .filter(Boolean)
              .join(", ")}
            .
          </Typography>
        </EmptyState>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(auto-fit, minmax(300px, 1fr))",
            },
            alignItems: "stretch",
          }}
        >
          {shown.map((a) => {
            const type = a.app_type || a.app;
            const live = deploy
              .deploymentsOf(type)
              .filter((d) => d.desiredState !== "absent").length;
            return (
              <AppCard
                key={a.app}
                app={a}
                entry={catalogFor(type)}
                deployments={deploy.deploymentsOf(type)}
                collections={collections}
                canInstall={tab !== 2 && Boolean(catalogFor(type)) && deploy.canWrite}
                canRemove={tab !== 2 && deploy.canWrite}
                onInstall={() => deploy.setChosen(catalogFor(type))}
                onRemove={deploy.setRemoving}
                onOpen={setOpen}
                onArchive={
                  tab === 0 && live === 0 && !deploy.failure
                    ? () => setArchiving(a)
                    : null
                }
                onUnarchive={tab === 2 ? () => unarchiveApp(type) : null}
                unarchiveBusy={unarchivingType === type}
              />
            );
          })}
        </Box>
      )}

      {/* An app Lopie publishes that this kernel holds no manifest for. It
          cannot be installed usefully — the deployment would have no
          permissions — and saying so beats leaving it off a list whose whole
          job is to be complete.

          One block naming all of them rather than an EmptyState each: the
          sentence is the same sentence every time, and repeating it once per
          app makes a register of what is missing look like a list of
          failures. */}
      {tab === 1 && missing.length ? (
        <EmptyState
          title={`${missing.length} published ${
            missing.length === 1 ? "app is" : "apps are"
          } not set up here`}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
              {missing.map((e) => (
                <Chip key={e.app} size="small" variant="outlined" label={e.name} />
              ))}
            </Stack>
            <Typography variant="body2">
              Lopie publishes {missing.length === 1 ? "it" : "them"}, and this
              Kernel holds no permissions — so anything installed would be
              allowed to read nothing. Listed rather than hidden, because a
              library that quietly drops what it cannot explain is one you
              cannot use to find out what exists.
            </Typography>
          </Stack>
        </EmptyState>
      ) : null}

      <AppDrawer
        app={open}
        entry={openType ? catalogFor(openType) : null}
        onClose={() => setOpen(null)}
        qa={
          open?.origin === "request" ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  Who can use it
                </Typography>
                <AppAccess appType={openType} get={get} post={post} links={ACCESS_LINKS} />
              </Box>
              <QaPanel appType={openType} get={get} post={post} />
            </>
          ) : null
        }
      >
        {open ? (
          <Deployments
            entry={openType ? catalogFor(openType) : null}
            origin={open?.origin}
            deployments={deploy.deploymentsOf(openType)}
            nameOf={deploy.nameOf}
            canRemove={deploy.canWrite}
            onRemove={deploy.setRemoving}
            onInstall={
              catalogFor(openType)
                ? () => deploy.setChosen(catalogFor(openType))
                : null
            }
            onUpdate={(instance, version) =>
              deploy.update(instance.name, version)
            }
            onAutoUpdate={(instance, enabled) =>
              deploy.setAutoUpdate(instance.name, enabled)
            }
            copies={copiesFor(openType)}
            onReplace={deploy.setAdopting}
            onRevert={deploy.setReverting}
          />
        ) : null}
      </AppDrawer>

      <RemoveDialog
        open={Boolean(deploy.removing)}
        instance={deploy.removing}
        busy={deploy.removeBusy}
        onClose={() => deploy.setRemoving(null)}
        onRemove={deploy.remove}
      />

      <AdoptDialog
        open={Boolean(deploy.adopting)}
        instance={deploy.adopting}
        copies={deploy.adopting ? copiesFor(deploy.adopting.app) : []}
        busy={deploy.removeBusy}
        onClose={() => deploy.setAdopting(null)}
        onPlan={deploy.planAdoption}
        onAdopt={deploy.adopt}
      />

      <RevertDialog
        open={Boolean(deploy.reverting)}
        instance={deploy.reverting}
        busy={deploy.removeBusy}
        onClose={() => deploy.setReverting(null)}
        onRevert={deploy.revert}
      />

      <InstallDialog
        open={Boolean(deploy.chosen)}
        entry={deploy.chosen}
        requirements={
          deploy.chosen ? deploy.requirementsFor(deploy.chosen.app) : []
        }
        onClose={() => deploy.setChosen(null)}
        onInstall={deploy.install}
      />

      <ArchiveDialog
        open={Boolean(archiving)}
        app={archiving}
        busy={archiveBusy}
        failure={archiveFailure}
        onClose={() => {
          setArchiving(null);
          setArchiveFailure(null);
        }}
        onArchive={archiveApp}
      />
    </Stack>
  );
}
